import React, { useState } from "react";
import { HiInformationCircle, HiDotsVertical } from "react-icons/hi";
import { Trans, t } from "@lingui/macro";
import { useWeb3React } from "@web3-react/core";
import useSWR from "swr";
import { BigNumber } from "ethers";

import Modal from "components/Modal/Modal";
import Button from "components/Button/Button";
import TierInfoTooltip from "./TierInfoTooltip";

import { formatAmount } from "lib/numbers";
import { contractFetcher } from "lib/contracts";
import { useChainId } from "../../lib/chains";
import { getContract } from "config/contracts";
import { PLACEHOLDER_ACCOUNT } from "lib/legacy";
import LiquidityEvent from "abis/events/LiquidityEvent.json";
import { PALM_DECIMALS } from "pages/Event/hooks/constants";

interface PreviewDestroyAmountResponse {
  indexes: BigNumber[];
  allocatedRewards: BigNumber[];
  savedRewards: BigNumber[];
  destroyedRewards: BigNumber[];
}

interface DestroyRewardsStat {
  index: BigNumber;
  allocatedReward: BigNumber;
  savedReward: BigNumber;
  destroyedReward: BigNumber;
}

const TierRow = ({ destroyRewardsStat }: { destroyRewardsStat: DestroyRewardsStat }) => {
  const { index, allocatedReward, savedReward, destroyedReward } = destroyRewardsStat;
  const [openTierModal, setOpen] = useState(false);

  return (
    <>
      <tr className="border-b relative" key={index.toString()}>
        <td className="text-[1.4rem] leading-[1.5] px-6 py-[1.2rem]">0{index.toNumber() + 1}</td>
        <td className="text-[1.4rem] leading-[1.5] px-6 py-[1.2rem]">
          {formatAmount(allocatedReward, PALM_DECIMALS, 2, true)}
        </td>
        <td className="text-[1.4rem] leading-[1.5] px-6 py-[1.2rem]">
          {formatAmount(savedReward, PALM_DECIMALS, 2, true)}
        </td>
        <td className="text-[1.4rem] text-red leading-[1.5] px-6 py-[1.2rem]">
          {formatAmount(destroyedReward, PALM_DECIMALS, 2, true)}
        </td>
        <td
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
          className="text-[1.4rem] leading-[1.5] px-6 py-[1.2rem] text-inactive cursor-pointer"
        >
          <HiDotsVertical />
        </td>

        {openTierModal && <TierInfoTooltip scheduleIndex={index} />}
      </tr>
    </>
  );
};

const SellPlpWarningModal = ({
  open,
  setOpen,
  sellPlp,
  plpAmount,
  isSubmitting
}: {
  open: boolean;
  setOpen: () => void;
  sellPlp: () => void;
  plpAmount: BigNumber;
  isSubmitting: boolean;
}) => {
  const { chainId } = useChainId();
  let { active, account, library } = useWeb3React();

  const liquidityEventAddress = getContract(chainId, "LiquidityEvent");
  const { data: previewDestroyAmounts } = useSWR<PreviewDestroyAmountResponse>(
    open ? [
      `LiquidityEvent:previewDestroyAmounts:${active}`,
      chainId,
      liquidityEventAddress,
      "previewDestroyAmounts",
      account || PLACEHOLDER_ACCOUNT,
    ] : null,
    contractFetcher(library, LiquidityEvent, [plpAmount])
  );

  const destroyRewardsStats = previewDestroyAmounts?.indexes.reduce<DestroyRewardsStat[]>(
    (stats, destroyAmountIndex, ind) => {
      const destroyRewardStat = {
        index: destroyAmountIndex,
        allocatedReward: previewDestroyAmounts.allocatedRewards[ind],
        savedReward: previewDestroyAmounts.savedRewards[ind],
        destroyedReward: previewDestroyAmounts.destroyedRewards[ind],
      };

      return [...stats, destroyRewardStat];
    },
    []
  );

  const infoText = t`If you sell PLP that you bought during the event, your PALM rewards will expire. You will no longer be able to claim them.`;

  return (
    <Modal className="Buy-plp-modal" isVisible={open} setIsVisible={setOpen} label={t`Event Rewards will be lost!`}>
      <div className="flex w-[42rem] flex-col tailwind isolate">
        <div className="border border-border rounded-[1rem] px-[1.4rem] py-[1.8rem] flex text-inactive gap-[0.8rem] text-[1.4rem] w-full">
          <HiInformationCircle />
          <div className="w-fit">{infoText}</div>
        </div>
      </div>

      <div className="@container/table tailwind isolate mt-[0.6rem]">
        <div className="max-h-none">
          <table className="w-full">
            <thead>
              <tr>
                <th className="first:rounded-l-sm bg-background-3 h-14 text-[1.2rem] leading-[1.5] text-inactive font-normal text-left px-6">
                  #
                </th>
                <th className="bg-background-3 h-14 text-[1.2rem] leading-[1.5] text-inactive font-normal text-left px-6">
                  <Trans>Total Rewards</Trans>
                </th>
                <th className="bg-background-3 h-14 text-[1.2rem] leading-[1.5] text-inactive font-normal text-left px-6">
                  <Trans>Saved Rewards</Trans>
                </th>
                <th className="bg-background-3 h-14 text-[1.2rem] leading-[1.5] text-inactive font-normal text-left px-6">
                  <Trans>You will loss</Trans>
                </th>
                <th className="last:rounded-r-sm bg-background-3 h-14 text-[1.2rem] leading-[1.5] text-inactive font-normal text-left px-6"></th>
              </tr>
            </thead>
            <tbody>
              {destroyRewardsStats?.map((destroyRewardsStat) => (
                <TierRow key={destroyRewardsStat.index.toString()} destroyRewardsStat={destroyRewardsStat} />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Button
        disabled={isSubmitting}
        variant={"red"}
        className="w-full mt-[1.6rem]"
        onClick={sellPlp}
      >
        <Trans>Confirm</Trans>
      </Button>
    </Modal>
  );
};

export default SellPlpWarningModal;
